import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { SocketContext } from '../../context/SocketContext';
import { AppContext } from '../../context/Context';

// Today's gameplan screensaver (bingo1.no/spilleplan layout) from local sockets; onEmpty → video fallback

const POLL_PLAN_MS = 600000;
const POLL_JACKPOTS_MS = 30000;
const TICK_MS = 30000;
// Crawl speed in px/s; duration derives from the distance
const CRAWL_PX_PER_SECOND = 18;
const CRAWL_MIN_SECONDS = 14;
// How long a hand-scrolled plan holds before the crawl resumes
const MANUAL_HOLD_MS = 12000;

// Current crawl offset, read from the computed transform matrix
const translateYOf = (el) => {
  const value = window.getComputedStyle(el).transform;
  if (!value || value === 'none') return 0;

  const parts = value.match(/-?[\d.]+/g);
  if (!parts) return 0;

  // matrix(a, b, c, d, tx, ty) or matrix3d(...16), ty is the 6th / 14th
  return Number(parts.length === 16 ? parts[13] : parts[5]) || 0;
};

// Self-service palette (LoginKiosk panels and gold)
const C = {
  ground: '#050505',
  panel: 'rgba(15, 19, 32, 0.85)',
  panelSoft: 'rgba(15, 19, 32, 0.55)',
  line: '#1d2540',
  text: '#f4f7ff',
  muted: '#8a9dad',
  gold: '#ffc727',
  goldSoft: 'rgba(255, 199, 39, 0.12)',
  goldLine: 'rgba(255, 199, 39, 0.35)',
  super: '#fc86c1',
  superSoft: 'rgba(252, 134, 193, 0.12)',
  superLine: 'rgba(252, 134, 193, 0.35)',
  blue: '#6f82ff',
};

const money = (value) =>
  new Intl.NumberFormat('nb-NO').format(Number(value) || 0);

// Times are zero-padded "HH:MM:SS", so a plain string compare is enough
const startTime = (game) => String(game?.dateStart ?? '').slice(0, 5);

const pad2 = (n) => String(n).padStart(2, '0');

const nowHHMM = () => {
  const now = new Date();
  return `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
};

const nowHHMMSS = () => {
  const now = new Date();
  return `${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(
    now.getSeconds()
  )}`;
};

// Own component so the 1s tick doesn't re-render the plan and restart the crawl
const Clock = () => {
  const [time, setTime] = useState(nowHHMMSS);

  useEffect(() => {
    const timer = setInterval(() => setTime(nowHHMMSS()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <span className="gp-clock">
      {time.slice(0, 5)}
      <span className="gp-clock-sec">:{time.slice(6)}</span>
    </span>
  );
};

const todayLong = () =>
  new Date().toLocaleDateString('nb-NO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

// 5x5 card with free centre: 24 pattern indexes, square 12 skipped
const PatternGrid = ({ pattern = [] }) => {
  const on = new Set(pattern ?? []);
  const cells = [];
  let index = 0;

  for (let position = 0; position < 25; position++) {
    if (position === 12) {
      cells.push(<i key={position} className="gp-cell is-free" />);
      continue;
    }
    cells.push(
      <i key={position} className={`gp-cell${on.has(index) ? ' is-on' : ''}`} />
    );
    index++;
  }

  return <div className="gp-grid">{cells}</div>;
};

const PatternBlock = ({ name, pattern }) => {
  if (!pattern) return null;

  const prizes = [pattern.prizeValue1, pattern.prizeValue2]
    .filter((value) => Number(value) > 0)
    .map(money)
    .join(' / ');
  const rops = [pattern.rop1, pattern.rop2]
    .filter((value) => Number(value) > 0)
    .join(' / ');

  const meta = [];
  if (prizes) meta.push(`${prizes} kr`);
  if (rops) meta.push(`rop ${rops}`);

  return (
    <div className="gp-pattern">
      <PatternGrid pattern={pattern.pattern} />
      <div>
        <span className="gp-pattern-name">{name}</span>
        <span className="gp-pattern-meta">{meta.join(' · ')}</span>
      </div>
    </div>
  );
};

const cardPrice = (game) => {
  if (Number(game.priceCard) > 0) return `${money(game.priceCard)} kr`;

  const prices = game.config?.priceCard ?? [];
  if (prices.length > 0) return `${prices.map(money).join(' / ')} kr`;

  return null;
};

const Facts = ({ game }) => {
  const items = [];

  if (game.config?.balls) items.push(['Baller', game.config.balls]);

  const price = cardPrice(game);
  if (price) items.push(['Kort', price]);

  if (Number(game.bingoValue) > 0)
    items.push(['Sluttgevinst', `${money(game.bingoValue)} kr`]);

  if ((game.prizes ?? []).length > 0)
    items.push(['Premier', `${game.prizes.map(money).join(' · ')} kr`]);

  return (
    <div className="gp-facts">
      {items.map(([label, value]) => (
        <span className="gp-fact" key={label}>
          <span>{label}</span> {value}
        </span>
      ))}
    </div>
  );
};

const STATUS_LABEL = { done: 'Ferdig', next: 'Neste', coming: 'Kommer' };

const GameRow = ({ game, status }) => (
  <li className={`gp-game is-${status}`}>
    <div className="gp-time">
      <span className="gp-time-start">{startTime(game)}</span>
      <span className="gp-time-buy">
        kjøp til {String(game.dateBuyEnd ?? '').slice(0, 5)}
      </span>
      <span className={`gp-status is-${status}`}>{STATUS_LABEL[status]}</span>
    </div>

    <div>
      <div className="gp-title">
        <h2 className="gp-name">{game.name}</h2>
        <span className="gp-game-id">#{game.gameID}</span>
        {game.isJackpot && <span className="gp-tag">Jackpot</span>}
        {game.isSuperJackpot && (
          <span className="gp-tag is-super">Kvikk Jackpot</span>
        )}
      </div>

      {game.description ? (
        <p className="gp-desc">{String(game.description).trim()}</p>
      ) : null}

      <Facts game={game} />

      <div className="gp-patterns">
        <PatternBlock name="Bingo" pattern={game.bingo} />
        <PatternBlock name="Ramme" pattern={game.ramme} />
        <PatternBlock name="Bilde" pattern={game.bilde} />
      </div>
    </div>
  </li>
);

const Jackpot = ({ label, jackpot, tone }) => {
  if (!jackpot) return null;

  return (
    <div className={`gp-jp is-${tone}`}>
      <span className="gp-jp-label">
        <i className="gp-live" />
        {label}
      </span>
      <span className="gp-jp-value">{money(jackpot.value)} kr</span>
      {jackpot.rop ? (
        <span className="gp-jp-rop">
          innen rop <b>{jackpot.rop}</b>
        </span>
      ) : null}
    </div>
  );
};

const Stat = ({ value, label, tone = '' }) => (
  <div className={`gp-stat ${tone}`}>
    <span className="gp-stat-value">{value}</span>
    <span className="gp-stat-label">{label}</span>
  </div>
);

function SpilleplanScreensaver({ onEmpty }) {
  const { socket } = useContext(SocketContext);
  const { hall } = useContext(AppContext);

  const [games, setGames] = useState(null);
  const [jackpots, setJackpots] = useState(null);
  // Only to re-run the render so "Neste" follows the clock without a refetch
  const [tick, setTick] = useState(0);

  const listRef = useRef(null);
  const olRef = useRef(null);
  // Where the crawl starts and where it ends, in pixels down the list
  const [crawl, setCrawl] = useState({ from: 0, to: 0 });
  // The plan is under the customer's finger instead of crawling on its own
  const [manual, setManual] = useState(false);
  const manualTimerRef = useRef(null);
  // Crawl offset when the finger landed, so the same row stays under it
  const handoffRef = useRef(0);

  useEffect(() => {
    if (!socket) return;

    const onPlan = (data) => setGames(Array.isArray(data) ? data : []);
    const onJackpots = (data) =>
      setJackpots(data && data.errorCode === 0 ? data : null);

    socket.on('getGamePlanPW', onPlan);
    socket.on('getJackpotsPW', onJackpots);

    const askPlan = () => socket.emit('getGamePlanPW');
    const askJackpots = () => socket.emit('getJackpotsPW', { linkID: 1 });

    askPlan();
    askJackpots();

    const planTimer = setInterval(askPlan, POLL_PLAN_MS);
    const jackpotTimer = setInterval(askJackpots, POLL_JACKPOTS_MS);
    const tickTimer = setInterval(() => setTick((n) => n + 1), TICK_MS);

    return () => {
      socket.off('getGamePlanPW', onPlan);
      socket.off('getJackpotsPW', onJackpots);
      clearInterval(planTimer);
      clearInterval(jackpotTimer);
      clearInterval(tickTimer);
    };
  }, [socket]);

  // Nothing to show is the caller's problem, not a blank screen
  useEffect(() => {
    if (games !== null && games.length === 0 && onEmpty) onEmpty();
  }, [games, onEmpty]);

  const sorted = [...(games ?? [])].sort((a, b) =>
    startTime(a).localeCompare(startTime(b))
  );

  // Hand back to the crawl from where the customer stopped; from the bottom it restarts at the top
  const release = useCallback(() => {
    const box = listRef.current;

    if (box) {
      const overflow = box.scrollHeight - box.clientHeight;
      const stopped = Math.max(0, Math.min(box.scrollTop, overflow));

      setCrawl({ from: overflow - stopped <= 20 ? 0 : stopped, to: overflow });
    }

    setManual(false);
  }, []);

  const holdManual = useCallback(() => {
    if (manualTimerRef.current) {
      window.clearTimeout(manualTimerRef.current);
    }

    manualTimerRef.current = window.setTimeout(release, MANUAL_HOLD_MS);
  }, [release]);

  const grab = useCallback(() => {
    if (!manual) {
      handoffRef.current = olRef.current ? -translateYOf(olRef.current) : 0;
      setManual(true);
    }

    holdManual();
  }, [manual, holdManual]);

  useEffect(
    () => () => {
      if (manualTimerRef.current) window.clearTimeout(manualTimerRef.current);
    },
    []
  );

  // Before paint, or the list jumps to the top for one frame on handover
  useLayoutEffect(() => {
    const box = listRef.current;
    if (!box) return;

    box.scrollTop = manual ? handoffRef.current : 0;
  }, [manual]);

  // Crawl tall lists from the next game (not the top), remeasured every tick
  useEffect(() => {
    if (manual) return;

    const list = listRef.current;
    if (!list) return;

    const overflow = list.scrollHeight - list.clientHeight;
    if (overflow <= 20) {
      setCrawl({ from: 0, to: 0 });
      return;
    }

    const next = list.querySelector('.gp-game.is-next');
    // Never past the end, and never so far that the last game scrolls off
    const from = next ? Math.min(next.offsetTop, overflow) : 0;

    setCrawl({ from, to: overflow });
    // Not keyed on `manual`, or release()'s resume point would be lost
  }, [games, tick]);

  if (games === null || sorted.length === 0) return null;

  const now = nowHHMM();
  let nextFound = false;
  const rows = sorted.map((game) => {
    let status = 'coming';
    if (startTime(game) <= now) {
      status = 'done';
    } else if (!nextFound) {
      nextFound = true;
      status = 'next';
    }
    return <GameRow key={game.gameID} game={game} status={status} />;
  });

  const starts = sorted.map(startTime).filter(Boolean).sort();

  return (
    <div className="gp-saver">
      <style>{css(crawl)}</style>

      <div className="gp-head-top">
        <span className="gp-badge">{hall || 'Spilleplan'}</span>
        <Clock />
      </div>

      <h1 className="gp-h1">Dagens spilleplan</h1>
      <p className="gp-date">{todayLong()}</p>

      {(jackpots?.jackpot || jackpots?.kvikk) && (
        <section className="gp-jackpots">
          <Jackpot label="Jackpot" jackpot={jackpots.jackpot} tone="jackpot" />
          <Jackpot
            label="Kvikk Jackpot"
            jackpot={jackpots.kvikk}
            tone="kvikk"
          />
        </section>
      )}

      <section className="gp-summary">
        <Stat value={sorted.length} label="Spill" />
        <Stat value={starts[0] ?? '–'} label="Første spill" />
        <Stat value={starts[starts.length - 1] ?? '–'} label="Siste spill" />
        <Stat
          value={sorted.filter((g) => g.isJackpot).length}
          label="Med jackpot"
          tone="is-jackpot"
        />
        <Stat
          value={sorted.filter((g) => g.isSuperJackpot).length}
          label="Kvikk Jackpot"
          tone="is-kvikk"
        />
      </section>

      <div
        className={`gp-listbox${manual ? ' is-manual' : ''}`}
        ref={listRef}
        onPointerDown={grab}
        onTouchStart={grab}
        onWheel={grab}
        onScroll={manual ? holdManual : undefined}
      >
        <ol
          ref={olRef}
          className={`gp-list${
            !manual && crawl.to > crawl.from ? ' is-crawling' : ''
          }`}
          style={{ transform: manual ? 'none' : `translateY(-${crawl.from}px)` }}
        >
          {rows}
        </ol>
      </div>
    </div>
  );
}

// Class rules rather than sx: 25 cells x 3 patterns per game
const css = ({ from, to }) => `
.gp-saver {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  padding: 3vh 4vw 0;
  color: ${C.text};
  font-family: inherit;
  overflow: hidden;
}
.gp-saver * { box-sizing: border-box; }
.gp-saver::before {
  content: '';
  position: absolute;
  inset: 0 0 auto;
  height: 60vh;
  background: radial-gradient(60rem 30rem at 50% 0%,
              rgba(255, 199, 39, 0.10) 0%, transparent 70%);
  pointer-events: none;
}
.gp-head-top {
  display: flex; align-items: center; justify-content: space-between;
  position: relative; z-index: 1;
}
.gp-badge {
  background: ${C.goldSoft}; border: 1px solid ${C.goldLine}; color: ${C.gold};
  border-radius: 999px; padding: 6px 18px; font-size: 20px; font-weight: 700;
  letter-spacing: .04em; text-transform: uppercase;
}
.gp-clock {
  font-size: 34px; font-weight: 800; font-variant-numeric: tabular-nums;
  color: ${C.text};
}
/* Smaller and dimmer: the seconds prove it is live, the minutes are the time */
.gp-clock-sec { font-size: 22px; color: ${C.muted}; font-weight: 700; }
.gp-h1 {
  position: relative; z-index: 1;
  margin: 14px 0 2px; font-size: 44px; font-weight: 800; color: ${C.gold};
}
.gp-date {
  position: relative; z-index: 1;
  margin: 0 0 16px; color: ${C.muted}; font-size: 20px;
  text-transform: capitalize;
}

.gp-jackpots { display: flex; gap: 14px; margin-bottom: 14px; position: relative; z-index: 1; }
.gp-jp {
  flex: 1; display: flex; flex-direction: column; gap: 2px;
  padding: 12px 18px; border-radius: 16px;
  background: ${C.goldSoft}; border: 1px solid ${C.goldLine};
}
.gp-jp.is-kvikk { background: ${C.superSoft}; border-color: ${C.superLine}; }
.gp-jp-label {
  display: flex; align-items: center; gap: 8px;
  font-size: 17px; font-weight: 700; color: ${C.gold};
  text-transform: uppercase; letter-spacing: .06em;
}
.gp-jp.is-kvikk .gp-jp-label { color: ${C.super}; }
.gp-live {
  width: 9px; height: 9px; border-radius: 50%; background: currentColor;
  animation: gpPulse 1.6s ease-in-out infinite;
}
@keyframes gpPulse { 0%,100% { opacity: 1 } 50% { opacity: .25 } }
.gp-jp-value { font-size: 40px; font-weight: 800; font-variant-numeric: tabular-nums; }
.gp-jp-rop { font-size: 16px; color: ${C.muted}; }

.gp-summary { display: flex; gap: 10px; margin-bottom: 16px; position: relative; z-index: 1; }
.gp-stat {
  flex: 1; padding: 10px 14px; border-radius: 14px;
  background: ${C.panelSoft}; border: 1px solid ${C.line};
  display: flex; flex-direction: column; gap: 2px;
}
.gp-stat.is-jackpot { border-color: ${C.goldLine}; }
.gp-stat.is-kvikk { border-color: ${C.superLine}; }
.gp-stat-value { font-size: 30px; font-weight: 800; font-variant-numeric: tabular-nums; }
.gp-stat-label { font-size: 15px; color: ${C.muted}; }

/* Always scrollable so the first swipe scrolls; the transform never changes scrollHeight */
.gp-listbox {
  flex: 1; min-height: 0; position: relative; z-index: 1;
  overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.gp-listbox::-webkit-scrollbar { width: 0; }
/* The bar appears only once it means something */
.gp-listbox.is-manual { scrollbar-width: thin; scrollbar-color: ${C.goldLine} transparent; }
.gp-listbox.is-manual::-webkit-scrollbar { width: 10px; }
.gp-listbox.is-manual::-webkit-scrollbar-track { background: transparent; }
.gp-listbox.is-manual::-webkit-scrollbar-thumb {
  background: ${C.goldLine}; border-radius: 999px;
}
/* Bottom padding clears the exit button IdleOverlay floats over this list */
.gp-list { list-style: none; margin: 0; padding: 0 0 calc(3vh + 110px); display: flex; flex-direction: column; gap: 12px; }
/* alternate + ease-in-out: walks down and back, settling at each end */
.gp-list.is-crawling { animation: gpCrawl ${Math.max(
  CRAWL_MIN_SECONDS,
  Math.round((to - from) / CRAWL_PX_PER_SECOND)
)}s ease-in-out infinite alternate; }
@keyframes gpCrawl {
  from { transform: translateY(-${from}px) }
  to { transform: translateY(-${to}px) }
}

.gp-game {
  display: grid; grid-template-columns: 150px 1fr; gap: 18px;
  padding: 14px 18px; border-radius: 16px;
  background: ${C.panel}; border: 1px solid ${C.line};
}
.gp-game.is-done { opacity: .42; }
.gp-game.is-next { border-color: ${C.goldLine}; background: ${C.goldSoft}; }
.gp-time { display: flex; flex-direction: column; gap: 3px; }
.gp-time-start { font-size: 32px; font-weight: 800; font-variant-numeric: tabular-nums; }
.gp-time-buy { font-size: 14px; color: ${C.muted}; }
.gp-status {
  margin-top: 4px; align-self: flex-start;
  font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em;
  color: ${C.muted}; border: 1px solid ${C.line}; border-radius: 999px; padding: 2px 10px;
}
.gp-status.is-next { color: ${C.gold}; border-color: ${C.goldLine}; }
.gp-title { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.gp-name { margin: 0; font-size: 24px; font-weight: 800; }
.gp-game-id { font-size: 15px; color: ${C.muted}; }
.gp-tag {
  font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em;
  color: ${C.gold}; background: ${C.goldSoft}; border: 1px solid ${C.goldLine};
  border-radius: 999px; padding: 2px 10px;
}
.gp-tag.is-super { color: ${C.super}; background: ${C.superSoft}; border-color: ${C.superLine}; }
.gp-desc { margin: 6px 0 0; font-size: 16px; color: ${C.muted}; }
.gp-facts { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
.gp-fact {
  font-size: 15px; background: ${C.panelSoft}; border: 1px solid ${C.line};
  border-radius: 10px; padding: 3px 10px;
}
.gp-fact > span { color: ${C.muted}; margin-right: 6px; }

.gp-patterns { display: flex; flex-wrap: wrap; gap: 18px; margin-top: 10px; }
.gp-pattern { display: flex; align-items: center; gap: 10px; }
.gp-grid {
  display: grid; grid-template-columns: repeat(5, 11px); gap: 2px;
}
.gp-cell {
  width: 11px; height: 11px; border-radius: 2px;
  background: rgba(255, 255, 255, 0.08);
}
.gp-cell.is-on { background: ${C.gold}; }
.gp-cell.is-free { background: ${C.blue}; }
.gp-pattern-name { display: block; font-size: 15px; font-weight: 700; }
.gp-pattern-meta { display: block; font-size: 13px; color: ${C.muted}; }
`;

export default SpilleplanScreensaver;
